import { useState, useEffect, useCallback } from "react";
import { onAuthStateChanged, signInWithEmailAndPassword, signOut, type User } from "firebase/auth";
import { doc, getDoc, collection, getDocs } from "firebase/firestore";
import { getFirebaseAuth, getFirebaseFirestore, isFirebaseAvailable } from "@/config/firebase";
import type { AuthState, AuthUser, MembershipInfo, UserProfile } from "@/types/auth";
import { setErrorTrackingUser, trackError } from "@/utils/error-tracking";
import { setAnalyticsUser, clearAnalyticsUser } from "@/utils/analytics-tracking";
import { isDemoMode, setDemoMode } from "@/demo/demoMode";
import { DEMO_USER } from "@/demo/demoData";
import { initDemoStore, isDemoStoreInitialized, clearDemoStore } from "@/demo/demoStore";

/** centerMemberships subcollection document type */
interface CenterMembershipDoc {
  centerOwnerUid: string;
  centerId: number;
  centerName: string;
  role: string;
  instructorId?: string;
  studiomateStaffId?: number;
  joinedAt: string;
}

const ACTIVE_CENTER_KEY_PREFIX = "vlab-instructor-active-center-";

function getStoredActiveCenterId(uid: string): number | null {
  const stored = localStorage.getItem(`${ACTIVE_CENTER_KEY_PREFIX}${uid}`);
  if (!stored) return null;
  const parsed = Number(stored);
  if (Number.isNaN(parsed)) return null;
  return parsed;
}

async function loadUserProfile(uid: string): Promise<AuthUser> {
  const db = getFirebaseFirestore();

  // 1. Load UserProfile from users/{uid}
  const profileSnap = await getDoc(doc(db, "users", uid));
  if (!profileSnap.exists()) {
    throw new Error("User profile not found in Firestore");
  }
  const profile = profileSnap.data() as UserProfile;

  // 2. Load ALL centerMemberships from users/{uid}/centerMemberships
  const membershipsRef = collection(db, "users", uid, "centerMemberships");
  const membershipsSnap = await getDocs(membershipsRef);
  if (membershipsSnap.empty) {
    throw new Error("No center membership found for user");
  }

  const memberships: MembershipInfo[] = membershipsSnap.docs.map((d) => {
    const data = d.data() as CenterMembershipDoc;
    return {
      centerOwnerUid: data.centerOwnerUid,
      centerId: data.centerId,
      centerName: data.centerName,
      instructorId: data.instructorId ?? "",
      ...(data.studiomateStaffId !== undefined
        ? { studiomateStaffId: data.studiomateStaffId }
        : {}),
    };
  });

  // 3. Determine activeMembership from localStorage preference
  const storedCenterId = getStoredActiveCenterId(uid);
  const activeMembership =
    (storedCenterId !== null
      ? memberships.find((m) => m.centerId === storedCenterId)
      : undefined) ?? memberships[0];

  return {
    uid,
    name: profile.name,
    role: profile.role,
    memberships,
    activeMembership,
  };
}

export function useAuth() {
  const [authState, setAuthState] = useState<AuthState>({ status: "loading" });
  const [isDemo, setIsDemo] = useState(isDemoMode());

  useEffect(() => {
    // If demo mode is active (e.g. page reload while demo), restore demo session
    if (isDemoMode()) {
      setIsDemo(true);
      setAuthState({ status: "authenticated", user: DEMO_USER });
      return;
    }

    // .env 없으면 Firebase 초기화 건너뛰고 unauthenticated 처리
    if (!isFirebaseAvailable()) {
      setAuthState({ status: "unauthenticated" });
      return;
    }

    const auth = getFirebaseAuth();
    let cancelled = false;

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser: User | null) => {
      if (cancelled) return;

      if (!firebaseUser) {
        setAuthState({ status: "unauthenticated" });
        return;
      }

      try {
        const user = await loadUserProfile(firebaseUser.uid);
        if (!cancelled) {
          setAuthState({ status: "authenticated", user });
          setErrorTrackingUser(user.uid, "instructor");
          setAnalyticsUser(user.uid, "instructor");
        }
      } catch (error) {
        // Firebase Auth 인증은 성공했으나 프로필/멤버십 로드에 실패한 경우.
        // 이전에는 조용히 unauthenticated 처리되어 로그인 화면으로만 돌아가
        // 원인을 알 수 없었으므로, 로깅 + 사용자 안내 메시지를 남긴다.
        console.error("[useAuth] 사용자 프로필 로드 실패:", error);
        trackError(error, { source: "useAuth.loadUserProfile", uid: firebaseUser.uid });
        if (!cancelled) {
          setAuthState({
            status: "unauthenticated",
            error:
              "로그인은 되었으나 계정 프로필을 불러오지 못했습니다. " +
              "센터 등록 상태를 확인하거나 관리자에게 문의해주세요.",
          });
        }
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const auth = getFirebaseAuth();
    await signInWithEmailAndPassword(auth, email, password);
  }, []);

  const loginAsDemo = useCallback(() => {
    setDemoMode(true);
    setIsDemo(true);
    if (!isDemoStoreInitialized()) {
      initDemoStore();
    }
    setAuthState({ status: "authenticated", user: DEMO_USER });
  }, []);

  const logout = useCallback(async () => {
    // Clear stored active center
    if (authState.status === "authenticated") {
      localStorage.removeItem(`${ACTIVE_CENTER_KEY_PREFIX}${authState.user.uid}`);
    }

    clearAnalyticsUser();

    if (isDemoMode()) {
      clearDemoStore();
      setDemoMode(false);
      setIsDemo(false);
      setAuthState({ status: "unauthenticated" });
      return;
    }
    const auth = getFirebaseAuth();
    await signOut(auth);
  }, [authState]);

  const setActiveCenterId = useCallback((centerId: number) => {
    setAuthState((prev) => {
      if (prev.status !== "authenticated") return prev;
      const membership = prev.user.memberships.find((m) => m.centerId === centerId);
      if (!membership) return prev;

      localStorage.setItem(`${ACTIVE_CENTER_KEY_PREFIX}${prev.user.uid}`, String(centerId));

      return {
        ...prev,
        user: { ...prev.user, activeMembership: membership },
      };
    });
  }, []);

  return { authState, login, loginAsDemo, logout, isDemo, setActiveCenterId };
}
